import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import "echarts/theme/macarons";

function EChartsGraph({ data, category, tooltipFormatter, redLines }) {
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      const myChart = echarts.init(chartRef.current, "macarons");

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "cross",
          },
          formatter: (params) => {
            return tooltipFormatter(params[0].value);
          },
        },
        xAxis: {
          type: "category",
          data: category,
        },
        yAxis: {
          type: "value",
          min: 0,
          max: undefined,
        },
        series: [],
        legend: {
          data: data && data.length === 2 ? ["최저 혈압", "최고 혈압"] : [],
        },
        grid: {
          left: "5%",
          right: "5%",
          bottom: "5%",
          top: "10%",
          containLabel: true,
        },
      };

      if (data && data.length === 2) {
        // 혈압 그래프 처리
        const minValue =
          Math.min(...(typeof data[0] === "number" ? [data[0]] : data[0]), ...(typeof data[1] === "number" ? [data[1]] : data[1])) > redLines[0].value
            ? redLines[0].value
            : Math.min(...(typeof data[0] === "number" ? [data[0]] : data[0]), ...(typeof data[1] === "number" ? [data[1]] : data[1]));
        const maxValue =
          Math.max(...(typeof data[0] === "number" ? [data[0]] : data[0]), ...(typeof data[1] === "number" ? [data[1]] : data[1])) < redLines[1].value
            ? redLines[1].value
            : Math.max(...(typeof data[0] === "number" ? [data[0]] : data[0]), ...(typeof data[1] === "number" ? [data[1]] : data[1]));
        const buffer = (maxValue - minValue) * 0.1;

        options.yAxis.min = Math.floor(minValue - buffer);
        options.yAxis.max = Math.ceil(maxValue + buffer);

        options.series.push(
          {
            data: data[0],
            type: "line",
            label: {
              show: true,
              position: "top",
              formatter: "{c}",
            },
            name: "최저 혈압", // 첫 번째 데이터 시리즈 이름
          },
          {
            data: data[1],
            type: "line",
            label: {
              show: true,
              position: "top",
              formatter: "{c}",
            },
            name: "최고 혈압", // 두 번째 데이터 시리즈 이름
          }
        );
      } else if (data && data.length > 0) {
        // 일반 그래프 처리
        if (redLines && redLines.length > 1) {
          const minValue = Math.min(...(typeof data === "number" ? [data] : data)) > redLines[0].value ? redLines[0].value : Math.min(...(typeof data === "number" ? [data] : data));
          const maxValue = Math.max(...(typeof data === "number" ? [data] : data)) < redLines[1].value ? redLines[1].value : Math.max(...(typeof data === "number" ? [data] : data));
          const buffer = (maxValue - minValue) * 0.1;

          options.yAxis.min = Math.floor(minValue - buffer);
          options.yAxis.max = Math.ceil(maxValue + buffer);
        } else {
          const minValue = Math.min(...(typeof data === "number" ? [data] : data)) > redLines[0].value ? redLines[0].value : Math.min(...(typeof data === "number" ? [data] : data));
          const maxValue = Math.max(...(typeof data === "number" ? [data] : data)) < redLines[0].value ? redLines[0].value : Math.max(...(typeof data === "number" ? [data] : data));
          const buffer = (maxValue - minValue) * 0.1;

          options.yAxis.min = Math.floor(minValue - buffer);
          options.yAxis.max = Math.ceil(maxValue + buffer);
        }
        options.series.push({
          data: data,
          type: "line",
          label: {
            show: true,
            position: "top",
            formatter: "{c}",
          },
        });
      }

      // 각 그래프에 빨간 선 추가
      if (redLines !== undefined && redLines && redLines.length > 0) {
        if (redLines.length > 1) {
          redLines.forEach((redLine, index) => {
            options.series.push({
              type: "line",
              name: `Red Line ${index + 1}`,
              data: new Array(category.length).fill(redLines[index].value),
              lineStyle: {
                color: redLine.color,
              },
              showSymbol: false,
            });
          });
        } else {
          redLines.forEach((redLine, index) => {
            options.series.push({
              type: "line",
              name: `Red Line ${index + 1}`,
              data: new Array(category.length).fill(redLine.value), // 빨간 선 값 배열 생성
              lineStyle: {
                color: redLine.color, // 빨간 선 색상 설정
              },
              showSymbol: false,
            });
          });
        }
      }

      myChart.setOption(options);

      // resize 이벤트 핸들러를 분리하여 참조를 유지
      const handleResize = () => {
        if (myChart && !myChart.isDisposed()) {
          myChart.resize();
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (myChart && !myChart.isDisposed()) {
          myChart.dispose(); // 컴포넌트가 언마운트될 때 차트 정리
        }
      };
    }
  }, [data, category, tooltipFormatter, redLines]);

  return <div ref={chartRef} style={{ width: "100%", height: "calc(100% - 50px)", maxHeight: "500px" }}></div>;
}

export default EChartsGraph;
